"use client";

import { useMutation } from "@apollo/client/react";
import {
  getCurrentTime,
  PlainDate,
  ZonedDateTime,
} from "@sa-nu/shared-utils/date";
import { useSearchParams } from "next/navigation";
import Script from "next/script";
import { useEffect, useMemo, useState } from "react";

import { graphql } from "../../../../__generated__/gql";
import type { BundleStayTicketsMutation } from "../../../../__generated__/gql/graphql";
import { ReservationHero } from "../../../_components/reservation/reservation-hero";
import { useNonce } from "../../../_contexts/nonce-context";
import { TicketReservationForm } from "../_components/ticket-reservation-form";

// チケットの最大数
const MAX_NUM_OF_CODE = 4;

// カバー画像の情報
export type TicketPromotionCoverImageInfo = {
  path: string;
  width: number;
  height: number;
  alt: string;
};

// プロモーションの情報
type TicketPromotionInfo = {
  // プロモーションコード
  // URL パラメータの promo に指定される
  code: string;
  // プロモーションに対応するカバー画像
  // mobile と desktop の2種類を指定する
  coverImage: {
    mobile: TicketPromotionCoverImageInfo;
    desktop: TicketPromotionCoverImageInfo;
  };
  // チケットフォームエリアを隠すかどうか
  hideTicketFormArea: boolean;
  // 対象期間
  // "2025-05-27T12:00:00+09:00[Asia/Tokyo]" のような形式で指定
  // null の場合には制限なし
  effectiveDateTimeRange: {
    start: string | null;
    end: string | null;
  };
};

// URL パラメータの promo の値によって画像のだしわけを行う
// ここに登録されていないコードの場合はデフォルトの挙動とする
const PROMOTIONS: TicketPromotionInfo[] = [
  // 2025年秋キャン後 LINE Stay自社予約
  {
    code: "stay-250919",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/stay-250919/mobile.png",
        height: 1974,
        width: 750,
        alt: "2025年 ゲスト宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/stay-250919/desktop.png",
        height: 2290,
        width: 2880,
        alt: "2025年 ゲスト宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: "2025-09-18T00:00:00+09:00[Asia/Tokyo]",
      end: null,
    },
  },
  // SANUフレンド限定
  {
    code: "sanu-friend",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/sanu-friend/mobile.png",
        height: 1974,
        width: 750,
        alt: "ご家族・ご友人限定のゲスト宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/sanu-friend/desktop.png",
        height: 1145,
        width: 1440,
        alt: "ご家族・ご友人限定のゲスト宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 2025年冬キャン後 LINE Stay自社予約
  {
    code: "stay-251218",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/stay-251218/mobile.svg",
        width: 402,
        height: 72,
        alt: "2025年 ゲスト宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/stay-251218/desktop.svg",
        width: 1280,
        height: 58,
        alt: "2025年 ゲスト宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // エリア/拠点/部屋タイプ/建築ページの予約 CTA 経由の Stay 自社予約
  {
    code: "stay-site-260709",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/stay-site-260709/mobile.svg",
        width: 402,
        height: 72,
        alt: "ゲスト宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/stay-site-260709/desktop.svg",
        width: 1280,
        height: 58,
        alt: "ゲスト宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // ゲスト宿泊予約ページ5000円OFF
  {
    code: "stay-260609",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/stay-260609/mobile.svg",
        width: 420,
        height: 50,
        alt: "ゲスト宿泊予約ページ5000円OFF",
      },
      desktop: {
        path: "/i/ticket-promotion/stay-260609/desktop.svg",
        width: 1280,
        height: 62,
        alt: "ゲスト宿泊予約ページ5000円OFF",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // ゲスト宿泊予約ページ3000円OFF
  {
    code: "stay-260828",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/stay-260828/mobile.svg",
        width: 420,
        height: 50,
        alt: "ゲスト宿泊予約ページ3000円OFF",
      },
      desktop: {
        path: "/i/ticket-promotion/stay-260828/desktop.svg",
        width: 1280,
        height: 62,
        alt: "ゲスト宿泊予約ページ3000円OFF",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 誕生日キャンペーン（4月）
  {
    code: "birthday-april",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/birthday-april/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/birthday-april/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 誕生日キャンペーン（5月）
  {
    code: "birthday-may",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/birthday-may/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/birthday-may/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 誕生日キャンペーン（6月）
  {
    code: "birthday-june",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/birthday-june/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/birthday-june/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 誕生日キャンペーン（7月）
  {
    code: "birthday-july",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/birthday-july/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/birthday-july/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 誕生日キャンペーン（8月）
  {
    code: "birthday-august",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/birthday-august/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/birthday-august/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 記念日キャンペーン（4月）
  {
    code: "anniversary-april",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/anniversary-april/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/anniversary-april/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 記念日キャンペーン（5月）
  {
    code: "anniversary-may",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/anniversary-may/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/anniversary-may/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 記念日キャンペーン（6月）
  {
    code: "anniversary-june",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/anniversary-june/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/anniversary-june/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 記念日キャンペーン（7月）
  {
    code: "anniversary-july",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/anniversary-july/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/anniversary-july/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 記念日キャンペーン（8月）
  {
    code: "anniversary-august",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/anniversary-august/mobile.jpg",
        width: 1500,
        height: 6500,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/anniversary-august/desktop.jpg",
        width: 5760,
        height: 3844,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 誕生日キャンペーン
  {
    code: "birthday",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/birthday/mobile.jpg",
        width: 1500,
        height: 1658,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/birthday/desktop.jpg",
        width: 5760,
        height: 1336,
        alt: "誕生日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // 記念日キャンペーン
  {
    code: "anniversary",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/anniversary/mobile.jpg",
        width: 1500,
        height: 1658,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/anniversary/desktop.jpg",
        width: 5760,
        height: 1336,
        alt: "記念日キャンペーン 宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
  // For Biz キャンペーン
  {
    code: "forbiz-260213",
    coverImage: {
      mobile: {
        path: "/i/ticket-promotion/forbiz-260213/mobile.jpg",
        width: 1500,
        height: 3948,
        alt: "For Business 面談参加者限定 お試し宿泊予約ページ",
      },
      desktop: {
        path: "/i/ticket-promotion/forbiz-260213/desktop.jpg",
        width: 5760,
        height: 3948,
        alt: "For Business 面談参加者限定 お試し宿泊予約ページ",
      },
    },
    hideTicketFormArea: true,
    effectiveDateTimeRange: {
      start: null,
      end: null,
    },
  },
];

const Mutation = graphql(`
  mutation BundleStayTickets($codes: [String!]!) {
    bundleStayTickets(codes: $codes) {
      stayTicketsValidationResult {
        ...StayTicketValidationResult
      }
      stayTicketUnit {
        ...StayTicketUnitForTicketReservationForm
      }
    }
  }
`);

export default function Page() {
  const nonce = useNonce();
  const params = useSearchParams();
  // code クエリだけに依存させる。params 全体を依存にすると、日付(ci/co)やフィルタ等の
  // nuqs 変化のたびに codes が再生成され、bundleStayTickets が無駄に再実行される
  // (その時チケットが Reserved 等だと unit=null になり真っ白になる)ため。
  const codeParams = params.get("code");
  const codes = useMemo(() => {
    return (
      codeParams
        ?.split("_")
        .filter((code) => code.length > 0)
        .slice(0, MAX_NUM_OF_CODE) ?? []
    );
  }, [codeParams]);

  const [data, setData] = useState<BundleStayTicketsMutation | null>(null);
  const [shouldOpenCodeArea, setShouldOpenCodeArea] = useState(false);

  const fromParam = useMemo(() => {
    const from = params.get("from");
    return from ? PlainDate.from(from) : undefined;
  }, [params]);

  const [bundleStayTickets, { loading }] = useMutation(Mutation);

  useEffect(() => {
    if (codes.length > 0) {
      bundleStayTickets({ variables: { codes } }).then(({ data }) => {
        setData(data ?? null);
      });
    } else {
      // コードの指定がない場合はコード入力エリアを開く
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize state
      setShouldOpenCodeArea(true);
    }
  }, [bundleStayTickets, codes]);

  // プロモーション対応
  const promotionInfo = PROMOTIONS.find((info) => {
    const code = params.get("promo");
    if (info.code !== code) return false;
    const start =
      info.effectiveDateTimeRange.start &&
      ZonedDateTime.from(info.effectiveDateTimeRange.start);
    const end =
      info.effectiveDateTimeRange.end &&
      ZonedDateTime.from(info.effectiveDateTimeRange.end);
    const current = getCurrentTime();
    return (
      (!start || ZonedDateTime.compare(start, current) <= 0) &&
      (!end || ZonedDateTime.compare(current, end) < 0)
    );
  });

  return (
    <>
      {/* HubSpotトラッキングコード */}
      <Script
        id="hs-script-loader"
        strategy="afterInteractive"
        src="//js-na2.hs-scripts.com/45622173.js"
        nonce={nonce}
      />

      <ReservationHero
        titles={["宿泊予約"]}
        coverImage={promotionInfo?.coverImage}
      />
      <TicketReservationForm
        validationResultData={
          data?.bundleStayTickets?.stayTicketsValidationResult
        }
        stayTicketUnitData={data?.bundleStayTickets?.stayTicketUnit ?? null}
        originalReservationData={null}
        loading={loading}
        shouldOpenCodeArea={shouldOpenCodeArea}
        ticketFormAreaHidden={promotionInfo?.hideTicketFormArea}
        fromDateOverride={fromParam}
        useNewRoomDetailFlow={true}
      />
    </>
  );
}
